import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { ImportedExtra } from '../../models/Bloodwork';
import { ImportedExtrasOffer, offerIsFullyAutomatic } from '../../models/ImportedExtrasPlacement';
import './imported-extras-notice.css';

/**
 * What a lab sent that this test could not hold, and the one button that fixes it.
 *
 * WHY THIS EXISTS ON THE ANALYSIS SCREEN AND NOT UNDER THE UPLOAD FORM. A hand
 * upload already says this: the note under Add Test Results names every row the
 * report carried that LabSmarts has no field for and tells the practitioner
 * they can put it in My Markers. That note works because the practitioner is
 * standing in front of the report when it appears.
 *
 * A delivered test has nobody standing there. It arrives on the chart on its
 * own, and the first time anyone looks at it is here. So the rows the lab sent
 * and nothing held were stored, silently, and the only place that could ever
 * mention them is the screen someone opens when the new test appears.
 *
 * Screen only, always. BloodworkTestAnalysis is rendered by bloodwork-display
 * and never by the PDF path, which builds its own sections in pdf-section.tsx,
 * so this cannot reach the client's document. That is not an accident worth
 * relying on quietly: this is a note to the practitioner about their own
 * library, and it would be meaningless in a client report.
 *
 * THE THREE GROUPS ARE THREE DIFFERENT AMOUNTS OF WORK, which is the whole
 * reason the copy is not one sentence:
 *
 *   - `fillable`: the marker is already in their library. Nothing to set up.
 *   - `addable`: LabSmarts knows the marker, so it can be added complete, with
 *     the units, precision and range mined off real reports.
 *   - `nameable`: LabSmarts does not know it. This one cannot be automatic, and
 *     saying so is better than inventing a range nobody chose. A marker with no
 *     range draws no bar and grades nothing, so a guess here would put a number
 *     on a chart and quietly mean nothing by it.
 */

const NAMES_LISTED = 6;

interface Props {
  offer: ImportedExtrasOffer;
  /**
   * Add the catalog definitions and place every value that can be placed.
   * Resolves once both the library and the test are saved.
   */
  onAdd: () => Promise<void>;
  /** Open My Markers, for the rows that need units and a range. */
  onOpenMyMarkers?: () => void;
}

const nameList = (rows: ImportedExtra[]): string[] => rows.map(row => row.name);

/**
 * "3 results" / "One result". Spelled out at one, because "1 result on this
 * test" reads like a count in a log and this is a sentence.
 */
export function countPhrase(n: number): string {
  return n === 1 ? 'One result' : `${n} results`;
}

/**
 * The headline: how many rows the lab sent that this test cannot show.
 *
 * Says CANNOT SHOW rather than "are not in My Markers", which is what the
 * upload note says. The two are the same fact from different ends, and here the
 * practitioner is looking at a chart and wondering whether it is complete, not
 * at a library and wondering what is in it.
 */
export function describeImportedExtras(total: number, someAreShipped = false): string {
  const head = `${countPhrase(total)} on this test ${total === 1 ? 'is' : 'are'} not charted`;
  // The reason is dropped as soon as one of these rows HAS a shipped field.
  // Saying "LabSmarts has no field for them" directly above a line saying
  // LabSmarts now reads them from the report contradicts itself, and the
  // per-group lines below give the real reason in either case.
  return someAreShipped
    ? `${head}.`
    : `${head}, because LabSmarts has no field for ${total === 1 ? 'it' : 'them'}.`;
}

/** What the button will do, in the practitioner's terms. */
export function describeAction(offer: ImportedExtrasOffer): string {
  const adding = offer.addable.length;
  const filling = offer.fillable.length;
  if (adding && filling) {
    return `${adding} can be added to My Markers for you, and ${filling} ${
      filling === 1 ? 'is' : 'are'
    } already in My Markers and just needs filling.`;
  }
  if (adding) {
    return adding === 1
      ? 'LabSmarts knows this one, so it can be added to My Markers for you, with its units and range already set up.'
      : `LabSmarts knows ${adding} of them, so they can be added to My Markers for you, with their units and ranges already set up.`;
  }
  return filling === 1
    ? 'It is already in My Markers, so the value just needs filling in.'
    : `They are already in My Markers, so the values just need filling in.`;
}

/**
 * The rows nobody can set up for them, and why not.
 *
 * The "come back" instruction is load-bearing. Defining a marker does not go
 * looking for values already delivered, so without this the practitioner adds
 * the marker, sees it empty, and reasonably concludes the import lost the row.
 */
/**
 * The rows LabSmarts itself now charts, and why they are still sitting here.
 *
 * This says UPLOAD THE REPORT AGAIN rather than offering a button, because
 * there is no honest button. Adding them to My Markers would give the
 * practitioner a second copy of a marker LabSmarts already draws, and filling
 * the shipped field from a stored leftover is a different feature from this
 * one. What is true is that the report has the number, this test does not, and
 * another pass at the report fixes it.
 *
 * Only ever seen where the shipped field is actually drawn. In prod both gated
 * families are held, so these rows come through as ordinary catalog markers
 * and this line never renders.
 */
export function describeNatively(count: number): string {
  return count === 1
    ? 'One of them is a marker LabSmarts now reads from the report itself, so it belongs on the chart rather than in My Markers. Upload this report again and it will chart with the rest.'
    : `${count} of them are markers LabSmarts now reads from the report itself, so they belong on the chart rather than in My Markers. Upload this report again and they will chart with the rest.`;
}

export function describeNameable(names: string[]): string {
  return names.length === 1
    ? 'One needs setting up by hand, because LabSmarts has no units or range for it. Add it in My Markers, then come back here and the value waiting on this test will fill in.'
    : `${names.length} need setting up by hand, because LabSmarts has no units or ranges for them. Add them in My Markers, then come back here and the values waiting on this test will fill in.`;
}

export function ImportedExtrasNotice({ offer, onAdd, onOpenMyMarkers }: Props) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [showAll, setShowAll] = useState(false);

  const actionable = [...offer.fillable, ...offer.addable.map(a => a.row)];
  const nativeRows = offer.natively.map(n => n.row);
  const total = actionable.length + offer.nameable.length + nativeRows.length;
  const canAct = actionable.length > 0;

  const listed = nameList([...actionable, ...offer.nameable, ...nativeRows]);
  const shown = showAll ? listed : listed.slice(0, NAMES_LISTED);

  const run = async () => {
    setBusy(true);
    setError(undefined);
    try {
      await onAdd();
    } catch (problem) {
      // Named rather than swallowed: the button's whole promise is that the
      // values are now on the chart, so a failed save has to say so or the
      // practitioner trusts a chart that did not change.
      setError(
        ((problem as Error) && (problem as Error).message) ||
          'Those markers could not be saved. Try again, and if it keeps failing let us know.',
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="imported-extras-notice" data-testid="imported-extras-notice">
      <div className="imported-extras-notice-head">
        {/* The string form, against the library App.tsx registers, which is what
            insights-review-notice.tsx does for the same icon. */}
        <FontAwesomeIcon icon="info-circle" className="imported-extras-notice-icon" />
        <span className="imported-extras-notice-headline">
          {describeImportedExtras(total, offer.natively.length > 0)}
        </span>
      </div>

      <ul className="imported-extras-notice-names">
        {shown.map(name => (
          <li key={name}>{name}</li>
        ))}
      </ul>
      {listed.length > NAMES_LISTED && (
        <button
          type="button"
          className="imported-extras-notice-more"
          onClick={() => setShowAll(!showAll)}
        >
          {showAll ? 'Show fewer' : `Show all ${listed.length}`}
        </button>
      )}

      {canAct && <div className="imported-extras-notice-line">{describeAction(offer)}</div>}

      {offer.natively.length > 0 && (
        <div className="imported-extras-notice-line imported-extras-notice-manual">
          {describeNatively(offer.natively.length)}
        </div>
      )}

      {offer.nameable.length > 0 && (
        <div className="imported-extras-notice-line imported-extras-notice-manual">
          {describeNameable(nameList(offer.nameable))}
          {onOpenMyMarkers && (
            <button
              type="button"
              className="imported-extras-notice-link"
              onClick={onOpenMyMarkers}
            >
              Open My Markers
            </button>
          )}
        </div>
      )}

      {error && <div className="imported-extras-notice-error">{error}</div>}

      {canAct && (
        <button
          type="button"
          className="imported-extras-notice-add"
          disabled={busy}
          onClick={run}
        >
          {busy
            ? 'Adding…'
            : offerIsFullyAutomatic(offer)
            ? `Add ${actionable.length === 1 ? 'it' : `all ${actionable.length}`} to My Markers`
            : `Add ${actionable.length} to My Markers`}
        </button>
      )}
    </div>
  );
}
